import { useState } from "react";
import { Search, CheckCircle2, Circle, Package, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import api, { inr } from "@/lib/api";

const FR_STAGES = ["submitted", "under_review", "approved", "onboarded"];
const stageLabel = { submitted: "Submitted", under_review: "Under Review", approved: "Approved", onboarded: "Onboarded", rejected: "Rejected" };

export default function Track() {
  const [ref, setRef] = useState("");
  const [type, setType] = useState("application");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!ref.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const path = type === "application" ? `/enquiries/track/${ref.trim()}` : `/orders/track/${ref.trim()}`;
      const { data } = await api.get(path);
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Not found");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-14">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-center">Track your status</h1>
      <p className="text-muted-foreground text-center mt-2">Enter your reference number to see progress.</p>

      <Tabs value={type} onValueChange={(v) => { setType(v); setResult(null); setError(""); }} className="mt-8">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="application" data-testid="tab-application"><FileText className="w-4 h-4 mr-2" /> Franchise Application</TabsTrigger>
          <TabsTrigger value="order" data-testid="tab-order"><Package className="w-4 h-4 mr-2" /> Order Request</TabsTrigger>
        </TabsList>

        <div className="mt-6 flex gap-2">
          <Input data-testid="track-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder={type === "application" ? "FR-XXXXXXXX" : "ORD-XXXXXXXX"} className="h-11 font-mono" onKeyDown={(e) => e.key === "Enter" && search()} />
          <Button onClick={search} disabled={loading} data-testid="track-btn" className="h-11 bg-yellow-400 hover:bg-yellow-500 text-slate-900 px-6"><Search className="w-4 h-4 mr-1" /> Track</Button>
        </div>

        {error && <p className="mt-6 text-center text-red-600" data-testid="track-error">{error}</p>}

        <TabsContent value="application">
          {result && (
            <div className="mt-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6" data-testid="track-result">
              <div className="flex justify-between items-center">
                <div><p className="text-sm text-muted-foreground">Applicant</p><p className="font-heading font-semibold text-lg">{result.full_name}</p></div>
                <Badge className="bg-yellow-200 text-yellow-800">{stageLabel[result.status]}</Badge>
              </div>
              {result.status !== "rejected" ? (
                <div className="mt-8 space-y-6">
                  {FR_STAGES.map((s) => {
                    const idx = FR_STAGES.indexOf(result.status);
                    const done = FR_STAGES.indexOf(s) <= idx;
                    return (
                      <div key={s} className="flex items-center gap-3">
                        {done ? <CheckCircle2 className="w-6 h-6 text-yellow-600" /> : <Circle className="w-6 h-6 text-slate-300" />}
                        <span className={`font-medium ${done ? "text-slate-900 dark:text-white" : "text-muted-foreground"}`}>{stageLabel[s]}</span>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="mt-6 text-red-600">This application was not approved. Please contact our franchise team.</p>}
            </div>
          )}
        </TabsContent>

        <TabsContent value="order">
          {result && (
            <div className="mt-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6" data-testid="track-result">
              <div className="flex justify-between items-center">
                <div><p className="text-sm text-muted-foreground">{result.store_name}</p><p className="font-heading font-semibold text-lg">{result.reference}</p></div>
                <Badge className="bg-yellow-200 text-yellow-800 capitalize">{result.status}</Badge>
              </div>
              <div className="mt-6 space-y-2">
                {result.items?.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm"><span>{it.name} × {it.qty}</span><span>{inr(it.price * it.qty)}</span></div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t flex justify-between font-heading font-bold"><span>Total</span><span>{inr(result.total)}</span></div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
